import {useCallback, useMemo} from 'react';

import {LocaleNameEnum} from '../../provider/locale/locale-context-type';
import {useUser} from '../../provider/user/user-hook';

import {
    addHiddenStatusCode,
    getReviewsHiddenStatusesConfig,
    getUserConfigWithHiddenStatuses,
} from './reviews-hidden-statuses-helper';
import {ReviewStatusType, useReviewStatusList} from './reviews-statuses';
import {getVisibleReviewStatuses} from './reviews-statuses-helper';

type UseVisibleReviewStatusesResultType = Readonly<{
    allStatuses: Array<ReviewStatusType>;
    visibleStatuses: Array<ReviewStatusType>;
    hiddenStatusCodes: Array<string>;
    hiddenStatusCodesSet: Set<string>;
    getStatusByCode: (statusCode: string) => ReviewStatusType | null;
    hideStatusCode: (statusCode: string) => Promise<void>;
    isStatusHidden: (statusCode: string) => boolean;
    isReviewStatusListInProgress: boolean;
    isSavingHiddenStatuses: boolean;
}>;

export function useVisibleReviewStatuses(localeName: LocaleNameEnum): UseVisibleReviewStatusesResultType {
    const {userConfig, mergeUserConfig, isInSavingUserConfig} = useUser();
    const {data: reviewStatusList, isFetching: isReviewStatusListInProgress} = useReviewStatusList(localeName);

    const hiddenStatusesConfig = useMemo(() => getReviewsHiddenStatusesConfig(userConfig), [userConfig]);

    const hiddenStatusCodesSet = useMemo(
        () => new Set(hiddenStatusesConfig.hiddenStatusCodes),
        [hiddenStatusesConfig.hiddenStatusCodes]
    );

    const allStatuses = useMemo(() => reviewStatusList?.statuses ?? [], [reviewStatusList?.statuses]);

    const visibleStatuses = useMemo(
        () => getVisibleReviewStatuses(allStatuses, hiddenStatusCodesSet),
        [allStatuses, hiddenStatusCodesSet]
    );

    const getStatusByCode = useCallback(
        (statusCode: string): ReviewStatusType | null => {
            return allStatuses.find((status) => status.statusCode === statusCode) ?? null;
        },
        [allStatuses]
    );

    const isStatusHidden = useCallback(
        (statusCode: string): boolean => {
            return hiddenStatusCodesSet.has(statusCode);
        },
        [hiddenStatusCodesSet]
    );

    const hideStatusCode = useCallback(
        async (statusCode: string): Promise<void> => {
            const nextConfig = addHiddenStatusCode(hiddenStatusesConfig.hiddenStatusCodes, statusCode);

            await mergeUserConfig(getUserConfigWithHiddenStatuses(userConfig, nextConfig));
        },
        [hiddenStatusesConfig.hiddenStatusCodes, mergeUserConfig, userConfig]
    );

    return {
        allStatuses,
        visibleStatuses,
        hiddenStatusCodes: hiddenStatusesConfig.hiddenStatusCodes,
        hiddenStatusCodesSet,
        getStatusByCode,
        hideStatusCode,
        isStatusHidden,
        isReviewStatusListInProgress,
        isSavingHiddenStatuses: isInSavingUserConfig,
    };
}
